import React, { useState, useCallback } from 'react';
import { IntlShape } from 'react-intl';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { FieldProps } from 'formik';
import { SingleDatePicker } from 'react-dates';
import moment from 'moment';
import { ReactComponent as IconCalendar } from '@whitelabel/media/icons/small/calendar.svg';
import { ReactComponent as IconChevronLeft } from '@whitelabel/media/icons/small/chevron-left.svg';
import { ReactComponent as IconChevronRight } from '@whitelabel/media/icons/small/chevron-right.svg';
import commonMsg from '@whitelabel/helpers/messages/commonMsg';
import { StyledSingleDatePickerInput } from './styled';

export interface ISingleDatePickerProps {
  id: string;
  className?: string;
  name: string;
  value: string;
  format?: string;
  minDate?: string;
  maxDate?: string;
  onChange: (...args: any[]) => any;
  onBlur: (...args: any[]) => any;
  form: FieldProps['form'];
  intl: IntlShape;
  keepTime?: boolean;
  invalid: boolean;
  hasSingleDateQuestion?: boolean;
  children?: JSX.Element;
}

const SingleDatePickerInput = ({
  id,
  name,
  value,
  className,
  format,
  minDate,
  maxDate,
  onChange,
  onBlur,
  form: { setFieldTouched, setFieldValue },
  intl,
  keepTime = false,
  invalid,
  hasSingleDateQuestion,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  children,
  ...props
}: ISingleDatePickerProps): JSX.Element => {
  const [focused, setFocused] = useState(false);

  let dateString = value;
  let time = '';
  if (keepTime) {
    dateString = value.substring(0, 10);
    time = value.substring(10);
  }

  const onFocusChange = useCallback(
    ({ focused: isFocused }) => {
      setFocused(isFocused);
      if (!isFocused && onBlur) onBlur(value);
    },
    [onBlur, value],
  );

  const onDateChange = useCallback(
    async (date) => {
      if (date) {
        let newValue: string;
        if (keepTime) {
          newValue = date.format('YYYY-MM-DD') + time;
        } else {
          newValue = date instanceof moment ? (date as moment.Moment).startOf('day').format(format) : '';
        }
        await setFieldValue(name, newValue);
        if (onChange) onChange(newValue);
      } else {
        await setFieldValue(name, '');
      }
      setFieldTouched(name, true);
    },
    [format, name, onChange, setFieldTouched, setFieldValue, keepTime, time],
  );

  const isOutsideRange = useCallback(
    (date) => {
      if (minDate && maxDate) {
        return !date.isBetween(minDate, maxDate, null, '[]');
      }

      if (minDate) return date.isBefore(minDate);
      if (maxDate) return date.isAfter(maxDate);
      return false;
    },
    [minDate, maxDate],
  );

  return (
    <StyledSingleDatePickerInput
      $rtl={intl.bidi}
      className={className}
      $focused={focused}
      $invalid={invalid}
      $hasSingleDateQuestion={hasSingleDateQuestion}
    >
      <SingleDatePicker
        {...props}
        id={id}
        date={dateString ? moment.utc(dateString, format) : null}
        placeholder={intl.formatMessage(commonMsg.singleDatePickerPlaceholder)}
        focused={focused}
        onDateChange={onDateChange}
        onFocusChange={onFocusChange}
        isOutsideRange={isOutsideRange}
        numberOfMonths={1}
        isRTL={intl.bidi}
        hideKeyboardShortcutsPanel
        showDefaultInputIcon
        displayFormat="ll"
        readOnly
        customInputIcon={<IconCalendar />}
        monthFormat="MMM YYYY"
        weekDayFormat="dd"
        navPrev={
          <div className="navSvg navPrev">
            <IconChevronLeft />
          </div>
        }
        navNext={
          <div className="navSvg navNext">
            <IconChevronRight />
          </div>
        }
      />
    </StyledSingleDatePickerInput>
  );
};

export default SingleDatePickerInput;
